/**
 * Phase 76.3 — Lightweight rich-text editor (no external dependencies).
 *
 * Uses contentEditable + document.execCommand for Bold / Italic / Bullet /
 * Numbered list / Link. The toolbar is minimal by design — we want a
 * predictable subset that mirrors the backend bleach whitelist exactly
 * (p, strong, em, b, i, u, ul, ol, li, br, a). Anything more elaborate is
 * silently scrubbed server-side. Browser-emitted <b>/<i> are normalised
 * to <strong>/<em> by the backend sanitiser so the persisted snapshot is
 * canonical.
 *
 * Props:
 *  • value:    HTML string (rehydrated on mount; never re-pushed during
 *              normal editing — see the `isFocused` guard).
 *  • onChange: called with the latest HTML on every input event.
 *  • testid:   prefix for data-testid attributes inside the toolbar.
 *  • placeholder: shown when the editor is empty.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from 'lucide-react';

const RichTextEditor = ({ value, onChange, testid = 'rte', placeholder = '' }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  // Phase 76.3 fix — preserve the user's selection range across
  // window.prompt(), which would otherwise blur the editor and destroy
  // the selection before createLink runs.
  const savedRangeRef = useRef(null);

  // Initial mount: paint the incoming HTML into the contentEditable surface.
  // We also re-sync when the editor is NOT focused (e.g. after a Generate
  // Draft Summary press writes new HTML into state). Doing it while
  // focused would clobber the caret position mid-typing.
  useEffect(() => {
    const el = editorRef.current;
    if (!el || isFocused) return;
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value, isFocused]);

  // Phase 76.3 fix — force execCommand to emit tag-based formatting
  // (<b>/<i>) rather than CSS-styled spans, so the server-side sanitiser
  // can normalise consistently. Browsers differ on this default.
  useEffect(() => {
    try {
      document.execCommand('styleWithCSS', false, false);
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch { /* old browsers: ignore */ }
  }, []);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const range = savedRangeRef.current;
    if (!range) return;
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  const exec = useCallback((cmd, arg = null) => {
    // execCommand is deprecated but still the lowest-friction way to do
    // basic rich-text editing without pulling a 100KB framework. The
    // backend bleach sanitizer is the actual security boundary.
    document.execCommand(cmd, false, arg);
    const html = editorRef.current?.innerHTML || '';
    onChange?.(html);
    editorRef.current?.focus();
  }, [onChange]);

  const onInput = () => {
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const insertLink = () => {
    // Capture selection BEFORE window.prompt() — opening the prompt blurs
    // the document and destroys the live Selection on some browsers /
    // Playwright builds.
    saveSelection();
    const selectedText = savedRangeRef.current ? savedRangeRef.current.toString() : '';
    const url = window.prompt('Paste link URL (https://…):', 'https://');
    if (!url) return;
    let safeUrl = url.trim();
    if (!/^(https?:|mailto:)/i.test(safeUrl)) safeUrl = `https://${safeUrl}`;
    // Re-focus the editor and restore the saved range so execCommand
    // operates on the user's selection instead of the prompt's residue.
    editorRef.current?.focus();
    restoreSelection();
    if (selectedText) {
      document.execCommand('createLink', false, safeUrl);
    } else {
      document.execCommand('insertHTML', false,
        `<a href="${safeUrl}">${safeUrl}</a>`);
    }
    onChange?.(editorRef.current?.innerHTML || '');
    editorRef.current?.focus();
  };

  // Phase 76.3 fix — defensive: flush latest innerHTML on blur so that
  // even if a prior onChange was missed (e.g. paste, IME), the parent
  // state is up-to-date when the user clicks Save.
  const onBlur = () => {
    setIsFocused(false);
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const Btn = ({ cmd, icon: Icon, title, suffix = '' }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}  // keep selection
      onClick={() => exec(cmd)}
      title={title}
      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
      data-testid={`${testid}-${cmd}${suffix}`}
    >
      <Icon size={13} />
    </button>
  );

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800" data-testid={testid}>
      <div className="flex items-center gap-1 border-b border-gray-100 dark:border-gray-700 px-1 py-1">
        <Btn cmd="bold" icon={Bold} title="Bold" />
        <Btn cmd="italic" icon={Italic} title="Italic" />
        <Btn cmd="insertUnorderedList" icon={List} title="Bullet list" suffix="-ul" />
        <Btn cmd="insertOrderedList" icon={ListOrdered} title="Numbered list" suffix="-ol" />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); saveSelection(); }}
          onClick={insertLink}
          title="Insert link"
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          data-testid={`${testid}-link`}
        >
          <LinkIcon size={13} />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onFocus={() => setIsFocused(true)}
        onBlur={onBlur}
        data-placeholder={placeholder}
        data-testid={`${testid}-editor`}
        className="prose prose-sm max-w-none px-3 py-2 text-xs text-gray-800 dark:text-gray-100 min-h-[80px] focus:outline-none rte-content"
      />
    </div>
  );
};

export default RichTextEditor;
